import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type {
  AssessmentAiAnswer,
  AssessmentAiVendor,
  AssessmentBuyerEvidence,
  AssessmentBuyerQuery,
  AssessmentDimension,
  AssessmentEvidence,
  AssessmentFinding,
  AssessmentPageIssue,
  AssessmentSeverity,
  AssessmentSpeed,
  SiteAgainAssessmentReportV1,
} from "@siteagain/shared-types";

import { assessmentCopy, type AssessmentCopy } from "./assessment-copy";
import { LOCALES, formatDay, setLocale, useLocale } from "./i18n";
import "./assessment-report.css";

type DimensionKey = keyof SiteAgainAssessmentReportV1["dimensions"];
type SectionId = "verdict" | "buyers" | "foundation" | "opportunities" | "evidence" | "action" | "pilot" | "limits";

const DIMENSION_ORDER: DimensionKey[] = ["seo", "geo", "performance", "maintainability"];
const SEVERITIES: AssessmentSeverity[] = ["critical", "warning", "opportunity"];
const SEVERITY_RANK: Record<AssessmentSeverity, number> = { critical: 0, warning: 1, opportunity: 2 };
const RISK_LEVELS = ["low", "medium", "high"] as const;
const CONFIDENCE_DOTS = { high: 3, medium: 2, low: 1, insufficient: 0 } as const;
const SECTION_ORDER: SectionId[] = ["verdict", "buyers", "foundation", "opportunities", "evidence", "action", "pilot", "limits"];
/**
 * A shared report (the `/r/<jobId>` link someone forwards) keeps the narrative
 * and drops the heavy instrumentation: the four technical lenses, the raw
 * evidence table and the limitations list. The studio and the pre-signup
 * assessment keep everything.
 */
const CONCISE_HIDDEN: SectionId[] = ["foundation", "evidence", "limits"];
const AI_VENDOR_LABELS: Record<AssessmentAiVendor, string> = {
  chatgpt: "ChatGPT",
  claude: "Claude",
  gemini: "Gemini",
  perplexity: "Perplexity",
  copilot: "Copilot",
  google_ai_mode: "Google AI Mode",
  other: "AI assistant",
};

/**
 * The audit surface, in the studio and before signup alike.
 *
 * The page answers the questions a site owner asks in order: can this be
 * migrated, how does the foundation stand, what can be improved, where does
 * the evidence come from, what happens next, and does a pilot fit. Every
 * figure comes from the assessment report. A lens the provider could not judge
 * is marked as not assessed and given no number; a missing report says so.
 * Nothing on this screen may be invented or estimated — see
 * docs/00-vision.md principle 5 and AUD-005/AUD-006.
 */
export function AssessmentReportView({
  report,
  host,
  onSelectFix,
  showLanguageSwitch = false,
  concise = false,
  pilotSlot,
  onStartGrowth,
}: {
  report: SiteAgainAssessmentReportV1;
  host: string;
  onSelectFix?: ((prompt: string) => void) | undefined;
  showLanguageSwitch?: boolean;
  /** Shared-report mode: narrative sections only, no raw instrumentation. */
  concise?: boolean;
  pilotSlot?: ReactNode;
  onStartGrowth?: (() => void) | undefined;
}) {
  const locale = useLocale();
  const t = assessmentCopy(locale);

  const visibleSections = useMemo(
    () => SECTION_ORDER.filter((id) => !(concise && CONCISE_HIDDEN.includes(id))),
    [concise],
  );
  const navSections: SectionId[] = visibleSections.filter((id) => id !== "limits");
  const sectionNumber = (id: SectionId) => navSections.indexOf(id) + 1;

  const findings = useMemo(
    () => [...report.findings].sort((left, right) =>
      SEVERITY_RANK[left.severity] - SEVERITY_RANK[right.severity]
      || left.title.localeCompare(right.title)),
    [report.findings],
  );
  const evidenceById = useMemo(
    () => new Map(report.evidence.map((item) => [item.id, item])),
    [report.evidence],
  );
  const findingById = useMemo(() => new Map(findings.map((item) => [item.id, item])), [findings]);
  const counts = useMemo(() => {
    const result: Record<AssessmentSeverity, number> = { critical: 0, warning: 0, opportunity: 0 };
    for (const finding of findings) result[finding.severity] += 1;
    return result;
  }, [findings]);

  const [filter, setFilter] = useState<AssessmentSeverity | "all">("all");
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set(findings[0] ? [findings[0].id] : []));
  const visibleFindings = filter === "all" ? findings : findings.filter((item) => item.severity === filter);

  const sectionRefs = useRef<Partial<Record<SectionId, HTMLElement | null>>>({});
  const [activeSection, setActiveSection] = useState<SectionId>("verdict");
  const assessedScores = DIMENSION_ORDER
    .map((key) => report.dimensions[key]?.score)
    .filter((score): score is number => typeof score === "number");
  const allScoresOver90 = assessedScores.length > 0 && assessedScores.every((score) => score >= 90);

  const topOpportunity = findings.find((finding) => finding.severity === "critical" || finding.severity === "warning");
  const topOpportunityUrl = topOpportunity?.affectedUrls[0];
  const safePreviewCount = findings.filter((finding) => finding.safetyClass === "safe_preview").length;
  const recommendedPageUrl = report.recommendedFirstPage.url;
  const hasRecommendedPage = !!recommendedPageUrl;
  const showSummary = findings.length > 0 || hasRecommendedPage;

  const eligibility = report.pilotEligibility;
  const pilot = eligibility
    ? eligibility.status === "strong"
      ? { tone: "strong" as const, ...t.pilotStrong }
      : eligibility.status === "needs_foundation"
        ? { tone: "needs_foundation" as const, ...t.pilotFoundation }
        : { tone: "insufficient_data" as const, ...t.pilotInsufficient }
    : { tone: "unavailable" as const, ...t.pilotUnavailable };

  useEffect(() => {
    const elements = visibleSections
      .map((id) => sectionRefs.current[id])
      .filter((element): element is HTMLElement => !!element);
    if (elements.length === 0 || typeof IntersectionObserver === "undefined") return;
    // The band sits just under the sticky navigation, so the section being read
    // is the one whose top has most recently crossed it.
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((left, right) => left.boundingClientRect.top - right.boundingClientRect.top);
      const id = visible[0]?.target.getAttribute("data-section") as SectionId | null;
      if (id) setActiveSection(id);
    }, { rootMargin: "-20% 0px -70% 0px" });
    for (const element of elements) observer.observe(element);
    return () => observer.disconnect();
  }, [visibleSections.join("|")]);

  function jumpTo(id: SectionId) {
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(id);
  }

  function toggleFinding(id: string) {
    setOpenIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function revealFinding(id: string) {
    setFilter("all");
    setOpenIds((current) => new Set(current).add(id));
    window.requestAnimationFrame(() => {
      document.getElementById(`finding-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  const verdict = t.verdict[report.qualification.status];
  const reportLanguage = report.locale?.slice(0, 2);
  const reportLanguageDiffers = !!reportLanguage && reportLanguage !== locale && !!t.languageName[reportLanguage];

  return (
    <div className="findings-view">
      <article className="ar" lang={locale}>
        <header className="ar-hero">
          <div className="ar-hero-top">
            <p className="ar-kicker">
              <span className="ar-kicker-dot" aria-hidden="true" />
              {t.kicker} · {formatDay(report.capturedAt, locale)}
            </p>
            {showLanguageSwitch && <LanguageSwitch />}
          </div>
          <h1>
            <span>{t.heroLead}</span>
            <strong>{host}</strong>
          </h1>
          <p className="ar-lede">{t.heroLede}</p>

          <p className="ar-meta-line">
            <span>{t.source}</span>
            <a href={report.sourceUrl} target="_blank" rel="noreferrer">{displayUrl(report.sourceUrl)}</a>
            <span aria-hidden="true">·</span>
            <span>{formatDay(report.capturedAt, locale)}</span>
          </p>

          {(report.partial || reportLanguageDiffers) && (
            <div className="ar-notices">
              {report.partial && (
                <p className="ar-notice warn">
                  <AlertGlyph />
                  <span>{t.partial}</span>
                  {!concise && <button type="button" onClick={() => jumpTo("limits")}>{t.partialLink}</button>}
                </p>
              )}
              {reportLanguageDiffers && reportLanguage && (
                <p className="ar-notice">
                  <GlobeGlyph />
                  <span>{t.reportLanguage(t.languageName[reportLanguage] ?? reportLanguage)}</span>
                </p>
              )}
            </div>
          )}
        </header>

        <nav className="ar-nav" aria-label={t.reportSections}>
          <ol>
            {navSections.map((id, index) => (
              <li key={id}>
                <button
                  type="button"
                  className={activeSection === id ? "active" : undefined}
                  aria-current={activeSection === id ? "true" : undefined}
                  onClick={() => jumpTo(id)}
                >
                  <small>{String(index + 1).padStart(2, "0")}</small>
                  {t.nav[id]}
                  {id === "opportunities" && findings.length > 0 && <em>{findings.length}</em>}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        {/* 01 — the answer first */}
        <section
          className={`ar-section ar-verdict tone-${report.qualification.status}`}
          data-section="verdict"
          ref={(element) => { sectionRefs.current.verdict = element; }}
          aria-labelledby="ar-verdict-title"
        >
          <div className="ar-verdict-main">
            <p className="ar-eyebrow">{t.verdictLabel}</p>
            <h2 id="ar-verdict-title">
              <VerdictGlyph status={report.qualification.status} />
              {verdict.headline}
            </h2>
            <p className="ar-verdict-detail">{verdict.detail}</p>
            {allScoresOver90 && (
              <div className="ar-high-scores-banner" role="region" aria-label={t.highScores.badge}>
                <div className="ar-high-scores-badge">
                  <SparkGlyph />
                  <span>{t.highScores.badge}</span>
                </div>
                <h3>{t.highScores.headline}</h3>
                <p>{t.highScores.detail}</p>
                <div className="ar-high-scores-growth">
                  <strong>{t.highScores.growthHighlight}</strong>
                </div>
              </div>
            )}
            {report.qualification.reasons.length > 0 && (
              <div className="ar-reasons">
                <p className="ar-eyebrow">{t.whyLabel}</p>
                <ul>
                  {report.qualification.reasons.map((reason) => <li key={reason}>{reason}</li>)}
                </ul>
              </div>
            )}
            {showSummary && (
              <div className="ar-so-what">
                <p className="ar-eyebrow">{t.soWhat.title}</p>
                <div className="ar-so-what-grid">
                  {(topOpportunity || hasRecommendedPage) && (
                    <div className="ar-so-what-item">
                      <small>{t.soWhat.topOpportunity}</small>
                      {topOpportunity
                        ? (
                          <>
                            <button type="button" className="ar-so-what-link" onClick={() => revealFinding(topOpportunity.id)}>
                              {topOpportunity.title}
                            </button>
                            {topOpportunityUrl && <code>{displayUrl(topOpportunityUrl)}</code>}
                          </>
                        )
                        : (
                          <>
                            <a className="ar-so-what-link" href={recommendedPageUrl} target="_blank" rel="noreferrer">
                              {displayUrl(recommendedPageUrl)}
                            </a>
                            <p>{report.recommendedFirstPage.reason}</p>
                          </>
                        )}
                    </div>
                  )}
                  <div className="ar-so-what-item">
                    <small>{t.soWhat.noRedesignLabel}</small>
                    <p>{findings.length > 0 ? t.soWhat.noRedesign(safePreviewCount, findings.length) : t.soWhat.noRedesignNone}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <aside className="ar-verdict-side">
            <div className="ar-risk">
              <p className="ar-eyebrow">{t.riskLabel}</p>
              <strong>{t.risk[report.qualification.risk]}</strong>
              <div
                className={`ar-risk-meter level-${report.qualification.risk}`}
                role="img"
                aria-label={`${t.riskLabel}: ${t.risk[report.qualification.risk]}`}
              >
                {RISK_LEVELS.map((level, index) => (
                  <i key={level} className={index <= RISK_LEVELS.indexOf(report.qualification.risk) ? "on" : undefined} />
                ))}
              </div>
              <div className="ar-risk-scale" aria-hidden="true">
                {RISK_LEVELS.map((level) => <span key={level}>{t.risk[level]}</span>)}
              </div>
            </div>
            <div className="ar-tally" aria-label={t.opportunitiesTitle}>
              {SEVERITIES.map((severity) => (
                <button
                  key={severity}
                  type="button"
                  className={`ar-tally-item sev-${severity}`}
                  disabled={counts[severity] === 0}
                  onClick={() => { setFilter(severity); jumpTo("opportunities"); }}
                >
                  <strong>{counts[severity]}</strong>
                  <span>{t.severityPlural[severity]}</span>
                </button>
              ))}
            </div>
          </aside>
        </section>

        {/* 02 — what buyers actually see */}
        <section
          className="ar-section"
          data-section="buyers"
          ref={(element) => { sectionRefs.current.buyers = element; }}
          aria-labelledby="ar-buyers-title"
        >
          <SectionHeading number={2} id="ar-buyers-title" title={t.buyers.title} lede={t.buyers.lede} />
          {report.buyerEvidence
            ? <BuyerEvidenceBlock evidence={report.buyerEvidence} t={t} concise={concise} />
            : <p className="ar-empty">{t.buyers.none}</p>}
        </section>

        {/* Four lenses — hidden in a shared report. */}
        {!concise && (
          <section
            className="ar-section"
            data-section="foundation"
            ref={(element) => { sectionRefs.current.foundation = element; }}
            aria-labelledby="ar-foundation-title"
          >
            <SectionHeading number={sectionNumber("foundation")} id="ar-foundation-title" title={t.foundationTitle} lede={t.foundationLede} />
            <div className="ar-lenses">
              {DIMENSION_ORDER.map((key) => (
                <LensCard key={key} dimension={report.dimensions[key]} label={t.dimensions[key].label} question={t.dimensions[key].question} t={t} />
              ))}
            </div>
            <p className="ar-footnote">{t.bands}</p>
          </section>
        )}

        {/* The shared report hides the technical section, so the four scores and
            what the site is built on travel with the summary the client reads. */}
        {concise && (
          <section className="ar-section" aria-labelledby="ar-scores-title">
            <SectionHeading number={sectionNumber("foundation")} id="ar-scores-title" title={t.scoresTitle} lede={t.scoresLede} />
            <div className="ar-scoreboard">
              {DIMENSION_ORDER.map((key) => (
                <ScoreCard key={key} dimension={report.dimensions[key]} label={t.dimensions[key].label} t={t} />
              ))}
            </div>
            {report.platform && (
              <p className="ar-platform">
                <span className="ar-platform-label">{t.platformLabel}</span>
                <strong>{t.platformDetected(report.platform.label)}</strong>
                <span>{report.platform.contentInHtml ? t.platformContentInHtml : t.platformContentInJs}</span>
              </p>
            )}
            <p className="ar-footnote">{t.bands}</p>
          </section>
        )}

        {/* 04 — findings */}
        <section
          className="ar-section"
          data-section="opportunities"
          ref={(element) => { sectionRefs.current.opportunities = element; }}
          aria-labelledby="ar-opportunities-title"
        >
          <SectionHeading
            number={sectionNumber("opportunities")}
            id="ar-opportunities-title"
            title={t.opportunitiesTitle}
            aside={t.opportunitiesCount(findings.length)}
            lede={findings.length > 0 ? t.opportunitiesLede : undefined}
          />

          {findings.length === 0 && <p className="ar-empty">{t.opportunitiesEmpty}</p>}

          {findings.length > 0 && (
            <>
              <div className="ar-severity-bar" aria-hidden="true">
                {SEVERITIES.filter((severity) => counts[severity] > 0).map((severity) => (
                  <i key={severity} className={`sev-${severity}`} style={{ flexGrow: counts[severity] }} />
                ))}
              </div>
              <div className="ar-filters" role="group" aria-label={t.opportunitiesTitle}>
                <button type="button" aria-pressed={filter === "all"} onClick={() => setFilter("all")}>
                  <span>{t.filterAll}</span><em>{findings.length}</em>
                </button>
                {SEVERITIES.map((severity) => (
                  <button
                    key={severity}
                    type="button"
                    className={`sev-${severity}`}
                    aria-pressed={filter === severity}
                    disabled={counts[severity] === 0}
                    onClick={() => setFilter(filter === severity ? "all" : severity)}
                  >
                    <i aria-hidden="true" />
                    <span>{t.severityPlural[severity]}</span>
                    <em>{counts[severity]}</em>
                    <small>{t.severityHint[severity]}</small>
                  </button>
                ))}
              </div>

              <ol className="ar-findings">
                {visibleFindings.map((finding, index) => (
                  <FindingRow
                    key={finding.id}
                    index={findings.indexOf(finding) + 1}
                    finding={finding}
                    evidence={finding.evidenceIds
                      .map((id) => evidenceById.get(id))
                      .filter((item): item is AssessmentEvidence => item !== undefined)}
                    open={openIds.has(finding.id)}
                    onToggle={() => toggleFinding(finding.id)}
                    onSelectFix={onSelectFix}
                    t={t}
                    locale={locale}
                    style={{ animationDelay: `${Math.min(index, 6) * 40}ms` }}
                  />
                ))}
                {visibleFindings.length === 0 && <li className="ar-empty">{t.filterEmpty}</li>}
              </ol>
            </>
          )}
        </section>

        {/* Where the findings come from — hidden in a shared report. */}
        {!concise && (
          <section
            className="ar-section"
            data-section="evidence"
            ref={(element) => { sectionRefs.current.evidence = element; }}
            aria-labelledby="ar-evidence-title"
          >
            <SectionHeading
              number={sectionNumber("evidence")}
              id="ar-evidence-title"
              title={t.evidenceTitle}
              aside={t.evidenceCount(report.evidence.length)}
              lede={report.evidence.length > 0 ? t.evidenceLede : undefined}
            />

            {report.evidence.length === 0 && <p className="ar-empty">{t.evidenceEmpty}</p>}

            {report.evidence.length > 0 && (
              <ul className="ar-evidence-list">
                {report.evidence.map((item) => {
                  const citedBy = findings.filter((finding) => finding.evidenceIds.includes(item.id));
                  return (
                    <li key={item.id}>
                      <p>{item.summary}</p>
                      <small>{item.source} · {t.evidenceKind[item.kind] ?? item.kind.replaceAll("_", " ")} · {formatDay(item.observedAt, locale)}</small>
                      {citedBy.length > 0 && (
                        <div className="ar-evidence-links">
                          {citedBy.map((finding) => (
                            <button
                              key={finding.id}
                              type="button"
                              className={`ar-trace sev-${finding.severity}`}
                              onClick={() => revealFinding(finding.id)}
                            >
                              <i aria-hidden="true" />{finding.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {/* 06 — what a proof would do */}
        <section
          className="ar-section"
          data-section="action"
          ref={(element) => { sectionRefs.current.action = element; }}
          aria-labelledby="ar-action-title"
        >
          <SectionHeading number={sectionNumber("action")} id="ar-action-title" title={t.actionTitle} lede={t.actionLede} />

          <div className="ar-next-grid">
            {hasRecommendedPage && (
              <div className="ar-target">
                <p className="ar-eyebrow">{t.recommendedPage}</p>
                <a href={recommendedPageUrl} target="_blank" rel="noreferrer" className="ar-target-url">
                  {displayUrl(recommendedPageUrl)}
                  <ExternalGlyph />
                </a>
                <p>{report.recommendedFirstPage.reason}</p>
                {/* The proof always rebuilds the homepage — it becomes the generated
                    site's entry page, so the invariant is architectural, not a
                    preference. Say so rather than let the recommendation read as a
                    promise about what gets built. */}
                {!isHomepageUrl(recommendedPageUrl) && (
                  <p className="ar-caveat"><AlertGlyph /><span>{t.homepageCaveat}</span></p>
                )}
              </div>
            )}

            {report.proofPlan.length > 0 && (
              <div className="ar-plan">
                <p className="ar-eyebrow">{t.proofPlanTitle}</p>
                <ol>
                  {report.proofPlan.map((step, index) => {
                    const source = findingById.get(step.findingId);
                    return (
                      <li key={`${step.findingId}-${index}`}>
                        <span className="ar-plan-index" aria-hidden="true">{index + 1}</span>
                        <div>
                          <strong>{step.intendedChange}</strong>
                          <p><small>{t.checkedBy}</small>{step.validation}</p>
                          {source && (
                            <button type="button" className={`ar-trace sev-${source.severity}`} onClick={() => revealFinding(source.id)}>
                              <i aria-hidden="true" />{source.title}
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </div>

          {report.materialChanges && report.materialChanges.length > 0 && (
            <div className="ar-material">
              <p className="ar-eyebrow">{t.materialTitle}</p>
              <div className="ar-material-grid">
                {report.materialChanges.map((item) => (
                  <article key={item.id}>
                    <span>{t.changeType[item.changeType] ?? item.changeType.replaceAll("_", " ")}</span>
                    <strong>{item.targetElement}</strong>
                    <p>{item.rationale}</p>
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* 07 — does a pilot fit */}
        <section
          className="ar-section"
          data-section="pilot"
          ref={(element) => { sectionRefs.current.pilot = element; }}
          aria-labelledby="ar-pilot-title"
        >
          <SectionHeading number={sectionNumber("pilot")} id="ar-pilot-title" title={t.pilotTitle} lede={t.pilotLede} />

          <div className={`ar-pilot-status tone-${pilot.tone}`}>
            <span className="ar-pilot-status-badge">{pilot.badge}</span>
            <h3>{pilot.headline}</h3>
            <p>{pilot.detail}</p>
            {eligibility?.status === "strong" && (
              pilotSlot != null
                ? <div className="ar-pilot-slot">{pilotSlot}</div>
                : <p className="ar-pilot-fallback">{t.pilotApplyFallback}</p>
            )}
          </div>

          <div className="ar-growth-card" aria-labelledby="ar-growth-title">
            <div className="ar-growth-card-head">
              <div>
                <span className="ar-growth-pill">{t.growthMonitor.badge}</span>
                <h3 id="ar-growth-title">{t.growthMonitor.headline}</h3>
              </div>
              <div className="ar-growth-price">
                <strong>{t.growthMonitor.price}</strong>
                <span>{t.growthMonitor.priceNote}</span>
              </div>
            </div>
            <p className="ar-growth-summary">{t.growthMonitor.summary}</p>
            <div className="ar-growth-features">
              <div className="ar-growth-col">
                <div className="ar-growth-col-header">
                  <span className="ar-growth-dot" />
                  <strong>{t.growthMonitor.baseTitle}</strong>
                </div>
                <p>{t.growthMonitor.baseDetail}</p>
              </div>
              <div className="ar-growth-col">
                <div className="ar-growth-col-header">
                  <span className="ar-growth-dot accent" />
                  <strong>{t.growthMonitor.recsTitle}</strong>
                </div>
                <p>{t.growthMonitor.recsDetail}</p>
              </div>
            </div>
            <p className="ar-growth-implements">{t.growthMonitor.implementsNote}</p>
            {onStartGrowth && (
              <button type="button" className="ar-growth-cta" onClick={onStartGrowth}>{t.growthMonitor.cta}</button>
            )}
          </div>

          <div className="ar-weekly-sample">
            <div className="ar-weekly-sample-head">
              <span className="ar-weekly-badge">{t.weeklySample.badge}</span>
              <h3>{t.weeklySample.title}</h3>
            </div>
            <article className="ar-weekly-item">
              <h4>{t.weeklySample.item.title}</h4>
              <p>{t.weeklySample.item.body}</p>
              <dl>
                <div>
                  <dt>{t.weeklySample.item.whyNowLabel}</dt>
                  <dd>{t.weeklySample.item.whyNow}</dd>
                </div>
                <div>
                  <dt>{t.weeklySample.item.potentialLabel}</dt>
                  <dd>{t.weeklySample.item.potential}</dd>
                </div>
              </dl>
            </article>
            <p className="ar-footnote">{t.weeklySample.footnote}</p>
          </div>
        </section>

        {/* Unnumbered limits — hidden in a shared report. */}
        {!concise && (
          <section
            className="ar-section ar-limits"
            data-section="limits"
            ref={(element) => { sectionRefs.current.limits = element; }}
            aria-labelledby="ar-limits-title"
          >
            <SectionHeading id="ar-limits-title" title={t.limitsTitle} />
            <ul>
              {report.limitations.map((item) => <li key={item}>{item}</li>)}
              <li>{t.limitsDisclaimer}</li>
            </ul>
          </section>
        )}
      </article>
    </div>
  );
}

/** The four buyer-facing blocks, in the order a buyer meets the business. */
function BuyerEvidenceBlock({ evidence, t, concise = false }: { evidence: AssessmentBuyerEvidence; t: AssessmentCopy; concise?: boolean }) {
  const hasAny = evidence.queries.length > 0
    || evidence.aiPrompts.length > 0
    || (!concise && (evidence.pageHealth !== null || evidence.speed !== null));
  if (!hasAny && evidence.unavailable.length === 0) return <p className="ar-empty">{t.buyers.none}</p>;

  return (
    <div className="ar-buyers">
      {evidence.queries.length > 0 && <RankingPanel queries={evidence.queries} t={t} />}
      {evidence.aiPrompts.length > 0 && <AiPanel prompts={evidence.aiPrompts} t={t} />}

      {!concise && (
        <div className="ar-buyers-duo">
          <SpeedPanel speed={evidence.speed} t={t} />
          <HealthPanel health={evidence.pageHealth} t={t} />
        </div>
      )}

      {evidence.unavailable.length > 0 && (
        <p className="ar-buyers-unavailable">
          <AlertGlyph />
          <span>
            {t.buyers.unavailableNote}{" "}
            {evidence.unavailable.map((code) => t.buyers.unavailableReason[code] ?? code).join(" · ")}
          </span>
        </p>
      )}
      <p className="ar-footnote">{t.buyers.footnote}</p>
    </div>
  );
}

function RankingPanel({ queries, t }: { queries: AssessmentBuyerQuery[]; t: AssessmentCopy }) {
  return (
    <div className="ar-buyers-panel">
      <div className="ar-buyers-panel-head">
        <h3>{t.buyers.rankTitle}</h3>
        <p>{t.buyers.rankLede}</p>
      </div>
      <div className="ar-query-grid">
        {queries.map((query) => (
          <article key={query.query} className={`ar-query${query.sitePosition === null ? " absent" : " present"}`}>
            <header>
              <span className="ar-query-search">
                <GoogleGlyph />
                <q>{query.query}</q>
              </span>
              <span className="ar-query-verdict">
                {query.sitePosition === null
                  ? t.buyers.rankNotTop(Math.max(query.results.length, 1))
                  : t.buyers.rankPosition(query.sitePosition)}
              </span>
            </header>
            <p className="ar-query-intent">{query.intent}</p>
            <p className="ar-serp-label">{t.buyers.rankResultsTitle}</p>
            <ol className="ar-serp">
              {query.results.slice(0, 5).map((result) => {
                const isYou = result.position === query.sitePosition;
                return (
                  <li key={result.url} className={isYou ? "you" : undefined}>
                    <span className="ar-serp-pos" aria-hidden="true">{result.position}</span>
                    <span className="ar-serp-body">
                      <a href={result.url} target="_blank" rel="noreferrer">{result.title}</a>
                      <small>{result.host}</small>
                    </span>
                    {isYou && <em>{t.buyers.rankYou}</em>}
                  </li>
                );
              })}
            </ol>
          </article>
        ))}
      </div>
    </div>
  );
}

function AiPanel({ prompts, t }: { prompts: AssessmentBuyerEvidence["aiPrompts"]; t: AssessmentCopy }) {
  return (
    <div className="ar-buyers-panel">
      <div className="ar-buyers-panel-head">
        <h3>{t.buyers.aiTitle}</h3>
        <p>{t.buyers.aiLede}</p>
      </div>
      <div className="ar-ai-prompts">
        {prompts.map((prompt) => (
          <div key={prompt.prompt} className="ar-ai-prompt">
            <q className="ar-ai-question">{prompt.prompt}</q>
            <div className="ar-ai-answers">
              {prompt.answers.map((answer) => (
                <AiAnswerCard key={`${answer.vendor}-${answer.model}`} answer={answer} t={t} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AiAnswerCard({ answer, t }: { answer: AssessmentAiAnswer; t: AssessmentCopy }) {
  const tone = answer.siteRecommended ? "good" : answer.siteMentioned ? "mention" : "absent";
  const badge = answer.siteRecommended
    ? t.buyers.aiRecommended
    : answer.siteMentioned
      ? t.buyers.aiMentioned
      : t.buyers.aiNotMentioned;
  return (
    <article className={`ar-ai-card tone-${tone}`}>
      <header>
        <span className="ar-ai-vendor">
          <AiVendorGlyph vendor={answer.vendor} />
          {AI_VENDOR_LABELS[answer.vendor]}
        </span>
        <span className="ar-ai-badge">{badge}</span>
      </header>
      <p className="ar-ai-answer">{answer.answer}</p>
      {answer.recommended.length > 0 && (
        <p className="ar-ai-named">
          <small>{t.buyers.aiNamedLabel}</small>
          {answer.recommended.slice(0, 4).map((name) => <span key={name}>{name}</span>)}
        </p>
      )}
    </article>
  );
}

function SpeedPanel({ speed, t }: { speed: AssessmentSpeed | null; t: AssessmentCopy }) {
  if (!speed) {
    return (
      <div className="ar-buyers-panel ar-buyers-small">
        <h3>{t.buyers.speedTitle}</h3>
        <p className="ar-empty">{t.buyers.speedUnavailable}</p>
      </div>
    );
  }
  const tone = speed.loadSeconds <= 2.5 ? "good" : speed.loadSeconds <= 4 ? "ok" : "slow";
  const verdict = tone === "good" ? t.buyers.speedGood : tone === "ok" ? t.buyers.speedOk : t.buyers.speedSlow;
  const max = 8;
  const fill = Math.max(4, Math.min(100, (speed.loadSeconds / max) * 100));
  return (
    <div className={`ar-buyers-panel ar-buyers-small ar-speed tone-${tone}`}>
      <h3>{t.buyers.speedTitle}</h3>
      <p className="ar-speed-lede">{t.buyers.speedLede}</p>
      <div className="ar-speed-readout">
        <strong>{t.buyers.speedSeconds(speed.loadSeconds)}</strong>
        <span>{verdict}</span>
      </div>
      <div className="ar-speed-track" role="img" aria-label={`${t.buyers.speedTitle}: ${t.buyers.speedSeconds(speed.loadSeconds)}`}>
        <i style={{ width: `${fill}%` }} />
        <b style={{ left: "31.25%" }} aria-hidden="true" />
        <b style={{ left: "50%" }} aria-hidden="true" />
      </div>
      <p className="ar-speed-detail">{t.buyers.speedDetail(speed.ttfbMs ?? 0, speed.requests ?? 0)}</p>
    </div>
  );
}

function HealthPanel({ health, t }: { health: AssessmentBuyerEvidence["pageHealth"]; t: AssessmentCopy }) {
  if (!health) {
    return (
      <div className="ar-buyers-panel ar-buyers-small">
        <h3>{t.buyers.healthTitle}</h3>
        <p className="ar-empty">{t.buyers.none}</p>
      </div>
    );
  }
  return (
    <div className="ar-buyers-panel ar-buyers-small ar-health">
      <h3>{t.buyers.healthTitle}</h3>
      <p className="ar-health-lede">
        {t.buyers.healthLede} <span className="ar-health-pages">{t.buyers.healthPages(health.pagesChecked)}</span>
      </p>
      {health.issues.length === 0
        ? <p className="ar-health-clean">{t.buyers.healthClean}</p>
        : (
          <>
            <ul className="ar-health-issues">
              {health.issues.map((issue) => <HealthIssue key={issue.key} issue={issue} t={t} />)}
            </ul>
            <p className="ar-health-fixable">{t.buyers.healthFixable}</p>
          </>
        )}
    </div>
  );
}

function HealthIssue({ issue, t }: { issue: AssessmentPageIssue; t: AssessmentCopy }) {
  const label = t.buyers.healthIssue[issue.key] ?? issue.key.replaceAll("_", " ");
  return (
    <li>
      <span className="ar-health-count">{issue.count}</span>
      <span className="ar-health-label">{label}</span>
      <small>{t.buyers.healthIssuesUnit(issue.pagesAffected, issue.pagesChecked)}</small>
    </li>
  );
}

function SectionHeading({ number, id, title, lede, aside }: { number?: number; id: string; title: string; lede?: string | undefined; aside?: string }) {
  return (
    <div className={`ar-heading${number === undefined ? " ar-heading-plain" : ""}`}>
      {number !== undefined && <span className="ar-heading-number" aria-hidden="true">{String(number).padStart(2, "0")}</span>}
      <div>
        <h2 id={id}>{title}{aside && <small>{aside}</small>}</h2>
        {lede && <p>{lede}</p>}
      </div>
    </div>
  );
}

/**
 * One score, sized for the shared report: the number, its band and the status
 * in one line, without the component breakdown that belongs to the full view.
 */
function ScoreCard({ dimension, label, t }: { dimension: AssessmentDimension; label: string; t: AssessmentCopy }) {
  const scored = dimension.status !== "not_assessed" && dimension.confidence !== "insufficient" && dimension.score !== undefined;
  const score = scored ? Math.max(0, Math.min(100, Math.round(dimension.score!))) : null;
  const band = score === null ? null : score <= 44 ? "weak" : score <= 74 ? "mid" : "solid";
  return (
    <article className={`ar-score status-${dimension.status}${band ? ` band-${band}` : ""}`}>
      <header>
        <h3>{label}</h3>
        <span className="ar-status">{t.status[dimension.status]}</span>
      </header>
      <div className="ar-score-value">
        {score !== null ? <strong>{score}<small>/100</small></strong> : <strong className="muted">{t.notScored}</strong>}
        {dimension.method === "public_proxy" && score !== null && <span className="ar-estimate">{t.publicEstimate}</span>}
      </div>
      <div
        className={`ar-gauge${score === null ? " empty" : ""}`}
        role={score !== null ? "meter" : undefined}
        aria-valuemin={score !== null ? 0 : undefined}
        aria-valuemax={score !== null ? 100 : undefined}
        aria-valuenow={score ?? undefined}
        aria-label={score !== null ? label : undefined}
      >
        {score !== null && <b style={{ width: `${score}%` }} />}
        <i style={{ left: "45%" }} aria-hidden="true" />
        <i style={{ left: "75%" }} aria-hidden="true" />
      </div>
    </article>
  );
}

function LensCard({ dimension, label, question, t }: { dimension: AssessmentDimension; label: string; question: string; t: AssessmentCopy }) {
  const scored = dimension.status !== "not_assessed" && dimension.confidence !== "insufficient" && dimension.score !== undefined;
  const score = scored ? Math.max(0, Math.min(100, Math.round(dimension.score!))) : null;
  const dots = CONFIDENCE_DOTS[dimension.confidence];
  const isProxy = dimension.method === "public_proxy";
  const weakest = isProxy && dimension.components
    ? [...dimension.components].sort((left, right) => left.score - right.score).slice(0, 3)
    : [];
  return (
    <article className={`ar-lens status-${dimension.status}`}>
      <header>
        <h3>{label}</h3>
        <span className="ar-status">{t.status[dimension.status]}</span>
      </header>
      <p className="ar-lens-question">{question}</p>

      <div className="ar-lens-score">
        {score !== null
          ? <strong>{score}<small>/100</small></strong>
          : <strong className="muted">{t.notScored}</strong>}
        {isProxy && score !== null && <span className="ar-estimate">{t.publicEstimate}</span>}
      </div>
      <div
        className={`ar-gauge${score === null ? " empty" : ""}`}
        role={score !== null ? "meter" : undefined}
        aria-valuemin={score !== null ? 0 : undefined}
        aria-valuemax={score !== null ? 100 : undefined}
        aria-valuenow={score ?? undefined}
        aria-label={score !== null ? label : undefined}
      >
        {score !== null && <b style={{ width: `${score}%` }} />}
        <i style={{ left: "45%" }} aria-hidden="true" />
        <i style={{ left: "75%" }} aria-hidden="true" />
      </div>

      <p className="ar-lens-summary">{dimension.summary}</p>

      {weakest.length > 0 && (
        <details className="ar-signals">
          <summary>{t.signalsToggle}</summary>
          <ul>
            {weakest.map((component) => (
              <li key={component.key}>
                <span className="ar-signal-head">
                  <span>{t.componentLabels[component.key] ?? component.key.replaceAll("_", " ")}</span>
                  <b>{Math.round(component.score)}</b>
                </span>
                {component.inputs && t.componentBasis[component.key] && (
                  <small>{t.componentBasis[component.key]!(component.inputs)}</small>
                )}
              </li>
            ))}
          </ul>
          <small className="ar-verified">{t.verifiedInPilot}</small>
        </details>
      )}

      <footer>
        <span className="ar-dots" aria-hidden="true">
          {[1, 2, 3].map((dot) => <i key={dot} className={dot <= dots ? "on" : undefined} />)}
        </span>
        <span>{t.confidence} {t.confidenceLevel[dimension.confidence]}</span>
      </footer>
    </article>
  );
}

function FindingRow({
  index,
  finding,
  evidence,
  open,
  onToggle,
  onSelectFix,
  t,
  locale,
  style,
}: {
  index: number;
  finding: AssessmentFinding;
  evidence: AssessmentEvidence[];
  open: boolean;
  onToggle(): void;
  onSelectFix?: ((prompt: string) => void) | undefined;
  t: AssessmentCopy;
  locale: ReturnType<typeof useLocale>;
  style: CSSProperties;
}) {
  const fixable = onSelectFix !== undefined && finding.safetyClass === "safe_preview";
  const panelId = `finding-panel-${finding.id}`;
  return (
    <li id={`finding-${finding.id}`} className={`ar-finding sev-${finding.severity}${open ? " open" : ""}`} style={style}>
      <button type="button" className="ar-finding-toggle" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
        <span className="ar-finding-index" aria-hidden="true">{String(index).padStart(2, "0")}</span>
        <span className="ar-severity"><i aria-hidden="true" />{t.severity[finding.severity]}</span>
        <strong>{finding.title}</strong>
        <span className="ar-category">{t.category[finding.category]}</span>
        <span className="ar-chevron" aria-hidden="true"><ChevronGlyph /></span>
        <span className="sr-only">{open ? t.collapse : t.expand}</span>
      </button>

      <div id={panelId} className="ar-finding-panel" hidden={!open}>
        {/* Plain first: what it means for the business, then the fix. The
            observation and its metrics stay available under the detail toggle
            so a non-technical owner is never made to read the raw evidence. */}
        <div className="ar-flow">
          <div>
            <small>{t.consequence}</small>
            <p>{finding.businessConsequence}</p>
          </div>
          <div className="ar-flow-fix">
            <small>{t.remediation}</small>
            <p>{finding.remediation}</p>
          </div>
        </div>

        <div className="ar-finding-meta">
          {fixable
            ? (
              <button
                type="button"
                className="ar-fix"
                onClick={() => onSelectFix?.(`${finding.remediation} (assessment finding ${finding.id}: ${finding.title})`)}
              >
                <SparkGlyph /> {t.proposeFix}
              </button>
            )
            : <span className="ar-safety">{t.safety[finding.safetyClass]}</span>}
        </div>

        <details className="ar-evidence ar-finding-tech">
          <summary>{t.technicalDetail}<ChevronGlyph /></summary>
          <div className="ar-flow">
            <div>
              <small>{t.observed}</small>
              <p>{finding.observation}</p>
            </div>
          </div>
          <div className="ar-finding-tags">
            <span className={`ar-priority sev-${finding.severity}`}>{t.priority[finding.severity]}</span>
            {finding.expectedSignal !== undefined && (
              <span className="ar-tag">
                <small>{t.signalLabel}</small>
                {t.signalLevel[finding.expectedSignal]}
              </span>
            )}
            {finding.effort !== undefined && (
              <span className="ar-tag">
                <small>{t.effortLabel}</small>
                {t.effortLevel[finding.effort]}
              </span>
            )}
          </div>
          <div className="ar-finding-meta">
            <span className="ar-dots" aria-hidden="true">
              {[1, 2, 3].map((dot) => <i key={dot} className={dot <= CONFIDENCE_DOTS[finding.confidence] ? "on" : undefined} />)}
            </span>
            <span>{t.confidence} {t.confidenceLevel[finding.confidence]}</span>
            <span className="sep" aria-hidden="true">·</span>
            <span>{finding.applicability === "observed" ? t.directlyObserved : t.likely}</span>
            {finding.affectedUrls.length > 0 && (
              <>
                <span className="sep" aria-hidden="true">·</span>
                <span className="ar-urls">
                  {finding.affectedUrls.slice(0, 2).map((url) => <code key={url}>{displayUrl(url)}</code>)}
                  {finding.affectedUrls.length > 2 && <span>{t.morePages(finding.affectedUrls.length - 2)}</span>}
                </span>
              </>
            )}
          </div>
        </details>

        {evidence.length > 0 && (
          <details className="ar-evidence">
            <summary>{t.evidence(evidence.length)}<ChevronGlyph /></summary>
            <ul>
              {evidence.map((item) => (
                <li key={item.id}>
                  <p>{item.summary}</p>
                  <small>{item.source} · {t.evidenceKind[item.kind] ?? item.kind.replaceAll("_", " ")} · {formatDay(item.observedAt, locale)}</small>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </li>
  );
}

export function LanguageSwitch({ className }: { className?: string }) {
  const locale = useLocale();
  const t = assessmentCopy(locale);
  return (
    <div className={`ar-lang${className ? ` ${className}` : ""}`} role="group" aria-label={t.languageLabel}>
      {LOCALES.map((option) => (
        <button
          key={option}
          type="button"
          lang={option}
          aria-pressed={locale === option}
          onClick={() => setLocale(option)}
        >
          {option.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function displayUrl(value: string): string {
  try {
    const url = new URL(value);
    const path = url.pathname === "/" ? "" : url.pathname;
    return `${url.hostname.replace(/^www\./, "")}${path}${url.search}`;
  } catch {
    return value;
  }
}

/** Treat an unparseable URL as the homepage: never show a caveat we can't justify. */
function isHomepageUrl(value: string): boolean {
  try { return new URL(value).pathname === "/"; } catch { return true; }
}

function VerdictGlyph({ status }: { status: SiteAgainAssessmentReportV1["qualification"]["status"] }) {
  if (status === "supported") return <svg className="ar-verdict-glyph" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="m7.5 12.5 3 3 6-6.5" /></svg>;
  if (status === "review_required") return <svg className="ar-verdict-glyph" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 7v6M12 16.5h.01" /></svg>;
  return <svg className="ar-verdict-glyph" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="m8.5 8.5 7 7M15.5 8.5l-7 7" /></svg>;
}
function AlertGlyph() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 2.8 20h18.4L12 3Z" /><path d="M12 9v5M12 17.5h.01" /></svg>; }
function GoogleGlyph() { return <svg className="ar-google" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5" /><path d="m15.8 15.8 4.2 4.2" /></svg>; }
function AiVendorGlyph({ vendor }: { vendor: AssessmentAiVendor }) {
  return <span className={`ar-ai-mark vendor-${vendor}`} aria-hidden="true">{AI_VENDOR_LABELS[vendor].slice(0, 1)}</span>;
}
function GlobeGlyph() { return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.5 3.8 5.5 3.8 9S14.5 18.5 12 21c-2.5-2.5-3.8-5.5-3.8-9S9.5 5.5 12 3Z" /></svg>; }
function ChevronGlyph() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5" /></svg>; }
function ExternalGlyph() { return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M8 5h7v7M15 5 7 13" /><path d="M13 10v5H5V7h5" /></svg>; }
function SparkGlyph() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3c.7 4.4 2.6 6.3 7 7-4.4.7-6.3 2.6-7 7-.7-4.4-2.6-6.3-7-7 4.4-.7 6.3-2.6 7-7Z" /></svg>; }
